import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './UserManagement.css';
import logger from './utils/logger';

/** 본사 조직 트리와 별도 — 슈퍼만 상위 없이 생성 */
const SIDE_ROLES_FE = ['dev', 'chief_major', 'representative'];

const RATE_CARD_COLLAPSED_KEY = 'userManagement.rateCompanyCards.collapsed';

function readCollapsedRateCompaniesFromStorage() {
  try {
    const raw = localStorage.getItem(RATE_CARD_COLLAPSED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr.filter((x) => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModal, setEditModal] = useState({ open: false, user: null, username: '', company_name: '', account: '', newPassword: '', parent_user_id: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [createModal, setCreateModal] = useState({ 
    open: false, 
    username: '', 
    password: '', 
    role: 'agency',
    company_name: '',
    account: '',
    parent_user_id: ''
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [rates, setRates] = useState([]);
  const [rateLoading, setRateLoading] = useState(false);
  const [rateError, setRateError] = useState(null);
  const [rateSaving, setRateSaving] = useState(false);
  const [rateModal, setRateModal] = useState({
    open: false,
    mode: 'create',
    id: null,
    role: 'agency',
    company: '',
    label: '',
    rate: ''
  });
  /** 접힌 분류명 — 미포함이면 펼침(디폴트). 새로고침 후에도 유지 */
  const [collapsedRateCompanies, setCollapsedRateCompanies] = useState(readCollapsedRateCompaniesFromStorage);

  useEffect(() => {
    try {
      localStorage.setItem(RATE_CARD_COLLAPSED_KEY, JSON.stringify([...collapsedRateCompanies]));
    } catch {
      /* ignore quota */
    }
  }, [collapsedRateCompanies]);

  const toggleRateCompanyCard = (company) => {
    setCollapsedRateCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(company)) next.delete(company);
      else next.add(company);
      return next;
    });
  };

  const isRateCompanyExpanded = (company) => !collapsedRateCompanies.has(company);

  useEffect(() => {
    // 현재 로그인한 사용자 정보 가져오기
    const user = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(user);
    fetchUsers();
    fetchCompanies();
    fetchRates();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // 권한은 서버가 토큰으로 판정한다. 클라이언트가 역할을 보내지 않는다.
      const response = await axios.get('/api/users');
      setUsers(response.data);
      setError(null);
    } catch (err) {
      logger.apiError('GET', '/api/users', err);
      setError(err.response?.data?.error || '사용자 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRates = async () => {
    try {
      setRateLoading(true);
      const user = JSON.parse(localStorage.getItem('user'));
      if (!['super', 'admin'].includes(user?.role)) {
        setRateLoading(false);
        return;
      }
      const response = await axios.get('/api/rates');
      setRates(response.data || []);
      setRateError(null);
    } catch (err) {
      logger.apiError('GET', '/api/rates', err);
      setRateError(err.response?.data?.error || '요율 목록을 불러오지 못했습니다.');
    } finally {
      setRateLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (!['super', 'admin'].includes(user?.role)) {
        return;
      }
      const response = await axios.get('/api/companies');
      setCompanies(response.data || []);
    } catch (err) {
      logger.apiError('GET', '/api/companies', err);
    }
  };

  const getUserDisplayName = (user) => user.company_name || user.company || user.username;

  const handleCreateUser = async () => {
    const { username, password, role, company_name, account, parent_user_id } = createModal;
    
    if (!username || !password || !role) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }
    const user = JSON.parse(localStorage.getItem('user'));
    if (SIDE_ROLES_FE.includes(role)) {
      if (user?.role !== 'super') {
        alert('개발/총대본/대표자 계정은 슈퍼어드민만 생성할 수 있습니다.');
        return;
      }
      if (parent_user_id !== '__none__') {
        alert('개발/총대본/대표자는 조직 트리와 별도입니다. 상위 사용자를 「없음 (본사·별도 역할 전용)」으로 선택하세요.');
        return;
      }
    } else if (!parent_user_id && role !== 'admin') {
      alert('상위 사용자를 선택해주세요.');
      return;
    }
    
    if (password.length < 6) {
      alert('비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }

    const sendParentId = parent_user_id === '__none__' || parent_user_id === '' ? null : parent_user_id;

    try {
      setCreateLoading(true);
      await axios.post('/api/users',
        { username, password, role, company_name: company_name || '', account, parent_user_id: sendParentId }
      );
      
      alert('사용자가 성공적으로 생성되었습니다.');
      closeCreateModal();
      fetchUsers(); // 사용자 목록 새로고침
    } catch (err) {
      logger.userAction('사용자 생성', { username, role }, err);
      alert(err.response?.data?.error || '사용자 생성에 실패했습니다.');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (editModal.newPassword && editModal.newPassword.length < 6) {
      alert('비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }

    try {
      setEditLoading(true);
      const updateData = {
        username: editModal.username || editModal.user?.username,
        // 빈 문자열도 그대로 보낸다. (예전에는 || 폴백 때문에 사용자명을 지울 수 없었다)
        company_name: editModal.company_name,
        account: editModal.account === '' ? null : editModal.account,
        parent_user_id: editModal.parent_user_id === '' ? null : editModal.parent_user_id
      };
      if (editModal.newPassword) {
        updateData.newPassword = editModal.newPassword;
      }
      
      await axios.put(`/api/users/${editModal.user.id}`,
        updateData
      );
      
      alert('사용자 정보가 성공적으로 수정되었습니다.');
      setEditModal({ open: false, user: null, username: '', company_name: '', account: '', newPassword: '' });
      fetchUsers(); // 사용자 목록 새로고침
    } catch (err) {
      logger.userAction('사용자 정보 수정', { userId: editModal.user.id }, err);
      alert(err.response?.data?.error || '사용자 정보 수정에 실패했습니다.');
    } finally {
      setEditLoading(false);
    }
  };

  // 사용자 삭제 함수
  const handleDeleteUser = async (user) => {
    const confirmMessage = `정말로 ${user.username} 사용자를 삭제하시겠습니까?\n\n⚠️ 주의: 계정만 삭제되며 되돌릴 수 없습니다.\n분류별 요율에 등록된 이 사용자명은 그대로 남으므로, 필요하면 요율도 함께 정리하세요.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const res = await axios.delete(`/api/users/${user.id}`);

      alert(res.data?.message || '사용자가 삭제되었습니다.');
      fetchUsers(); // 사용자 목록 새로고침
    } catch (err) {
      logger.userAction('사용자 삭제', { userId: user.id }, err);
      alert(err.response?.data?.error || '사용자 삭제에 실패했습니다.');
    }
  };

  const getDefaultParentId = (role) => {
    if (currentUser && getParentOptions(role).some((user) => user.id === currentUser.id)) {
      return currentUser.id;
    }
    return '';
  };

  const openCreateModal = () => {
    const opts = getParentOptionsForCreate();
    const defaultParentId = (() => {
      if (currentUser?.role === 'admin' && opts.some((u) => u.id === currentUser.id)) return String(currentUser.id);
      if (currentUser?.role === 'super' && opts.length === 0) return '__none__';
      if (currentUser?.role === 'super' && opts.length > 0) return String(opts.find((u) => u.role === 'admin')?.id || opts[0]?.id || '');
      return opts[0] ? String(opts[0].id) : '';
    })();
    const childRoles = getChildRolesForParent(defaultParentId === '__none__' ? '' : defaultParentId);
    setCreateModal({
      open: true,
      username: '',
      password: '',
      role: childRoles[0] || '',
      company_name: '',
      account: '',
      parent_user_id: defaultParentId
    });
  };

  const closeCreateModal = () => {
    setCreateModal({ open: false, username: '', password: '', role: 'agency', company_name: '', account: '', parent_user_id: '' });
  };

  const openRateModal = (rate = null, companyOverride = null) => {
    if (rate) {
      setRateModal({
        open: true,
        mode: 'edit',
        id: rate.id,
        role: rate.role,
        company: rate.company,
        label: rate.label,
        rate: String(rate.rate ?? '')
      });
    } else {
      setRateModal({
        open: true,
        mode: 'create',
        id: null,
        role: 'agency',
        company: companyOverride || '',
        label: '',
        rate: ''
      });
    }
  };

  const closeRateModal = () => {
    setRateModal({ open: false, mode: 'create', id: null, role: 'agency', company: '', label: '', rate: '' });
  };

  const handleSaveRate = async () => {
    const { role, company, label, rate } = rateModal;
    const parsedRate = parseFloat(rate);

    if (!role || !company || !label || Number.isNaN(parsedRate)) {
      alert('모든 필드를 입력해주세요.');
      return;
    }
    if (parsedRate < 0 || parsedRate > 100) {
      alert('요율은 0%에서 100% 사이의 값이어야 합니다.');
      return;
    }

    try {
      setRateSaving(true);
      if (rateModal.mode === 'edit') {
        await axios.put(`/api/rates/${rateModal.id}`, { role, company, label, rate: parsedRate });
      } else {
        await axios.post('/api/rates', { role, company, label, rate: parsedRate });
      }
      closeRateModal();
      fetchRates();
    } catch (err) {
      logger.userAction('요율 저장', { role, company, label, rate: parsedRate }, err);
      alert(err.response?.data?.error || '요율 저장에 실패했습니다.');
    } finally {
      setRateSaving(false);
    }
  };

  const [resetSettlementLoading, setResetSettlementLoading] = useState(null);

  const handleResetSettlementByCompany = async (company) => {
    // 되돌릴 수 없고 전체 기간이 대상이므로 분류명을 직접 입력하게 한다.
    const typed = window.prompt(
      `[${company}] 분류의 일별정산 입력과 정산 상태를 전체 기간에 걸쳐 삭제합니다.\n` +
      '이 작업은 되돌릴 수 없습니다.\n\n' +
      `계속하려면 분류명 "${company}" 을(를) 그대로 입력하세요.`
    );
    if (typed === null) return;
    if (typed.trim() !== company) {
      alert('분류명이 일치하지 않아 초기화를 취소했습니다.');
      return;
    }

    try {
      setResetSettlementLoading(company);
      const params = new URLSearchParams({ company, confirm: company });
      const res = await axios.delete(`/api/settlement/inputs/by-company?${params}`);
      const deleted = res.data?.affectedRows ?? 0;
      alert(`일별정산이 초기화되었습니다. (삭제된 입력 ${deleted}건)`);
    } catch (err) {
      logger.apiError('DELETE', '/api/settlement/inputs/by-company', err);
      alert(err.response?.data?.error || '초기화에 실패했습니다.');
    } finally {
      setResetSettlementLoading(null);
    }
  };

  const handleDeleteRate = async (rate) => {
    if (!window.confirm(`정말로 ${rate.label} 요율을 삭제하시겠습니까?`)) {
      return;
    }
    try {
      await axios.delete(`/api/rates/${rate.id}`);
      fetchRates();
    } catch (err) {
      logger.userAction('요율 삭제', { id: rate.id }, err);
      alert(err.response?.data?.error || '요율 삭제에 실패했습니다.');
    }
  };

  const companySections = useMemo(() => {
    return companies.map((c) => c.name).filter(Boolean);
  }, [companies]);

  const formatRateTotalPercent = (values) => {
    const total = values.reduce((acc, r) => acc + (Number(r.rate) || 0), 0);
    if (!Number.isFinite(total)) return '0';
    return String(parseFloat(total.toFixed(4)));
  };

  const openEditModal = (user) => {
    setEditModal({
      open: true,
      user,
      username: user.username || '',
      company_name: user.company_name || '',
      account: user.account || '',
      newPassword: '',
      parent_user_id: user.parent_user_id || ''
    });
  };

  const closeEditModal = () => {
    setEditModal({ open: false, user: null, username: '', company_name: '', account: '', newPassword: '', parent_user_id: '' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // UTC 시간에 9시간을 더해서 한국시간으로 변환
    const koreanTime = new Date(date.getTime() + (9 * 60 * 60 * 1000));
    return koreanTime.toLocaleString('ko-KR');
  };

  const ORG_CHAIN_FE = ['admin', 'major_branch', 'branch', 'jisa', 'distributor', 'agency'];

  const validateParentChildRolesFE = (parentRole, childRole) => {
    if (parentRole === 'super' && childRole === 'admin') return true;
    // 개발/총대본/대표자는 조직 트리와 별도라 상위를 둘 수 없다. (서버 규칙과 동일)
    if (SIDE_ROLES_FE.includes(childRole)) return false;
    const pi = ORG_CHAIN_FE.indexOf(parentRole);
    const ci = ORG_CHAIN_FE.indexOf(childRole);
    if (pi === -1 || ci === -1) return false;
    return pi < ci;
  };

  const getParentOptions = (childRole, excludeUserId) => {
    return users.filter((user) => {
      if (excludeUserId && user.id === excludeUserId) return false;
      return validateParentChildRolesFE(user.role, childRole);
    });
  };

  /** 상위 사용자(parent_user_id)를 기준으로 선택 가능한 자식 역할 목록 반환 */
  const getChildRolesForParent = (parentUserId) => {
    if (!parentUserId || parentUserId === '__none__') {
      if (currentUser?.role === 'super') {
        return ['admin', 'dev', 'chief_major', 'representative'];
      }
      return [];
    }
    const parentUser = users.find((u) => String(u.id) === String(parentUserId));
    if (!parentUser) return [];
    const parentRole = parentUser.role;
    // 본사 직속은 조직(대본사~매장)만. 개발·총대본·대표자는 슈퍼가 상위 「없음」으로만 생성
    const childRolesMap = {
      super: ['admin'],
      admin: ['major_branch', 'branch', 'jisa', 'distributor', 'agency'],
      major_branch: ['branch', 'jisa', 'distributor', 'agency'],
      branch: ['jisa', 'distributor', 'agency'],
      jisa: ['distributor', 'agency'],
      distributor: ['agency'],
      agency: [],
      dev: [],
      chief_major: [],
      representative: []
    };
    return childRolesMap[parentRole] || [];
  };

  /** 상위 사용자 선택 시 사용할 수 있는 상위 사용자 목록 (계층 상위만, 개발 계정 제외) */
  const getParentOptionsForCreate = () => {
    const parentRoles = ['admin', 'major_branch', 'branch', 'jisa', 'distributor'];
    return users.filter((user) => parentRoles.includes(user.role));
  };

  /** 사용자(계정) 역할 표시 — 조직 트리 명칭 */
  const getUserRoleLabel = (role) => {
    switch (role) {
      case 'super': return '슈퍼어드민';
      case 'admin': return '본사';
      case 'dev': return '개발';
      case 'chief_major': return '총대본';
      case 'representative': return '대표자';
      case 'major_branch': return '대본사';
      case 'branch': return '부본사';
      case 'jisa': return '지사';
      case 'distributor': return '총판';
      case 'agency': return '매장';
      default: return role;
    }
  };

  /** 분류별 요율 rate_settings 행 표시용 (DB role은 동일, 라벨만 구분) */
  const getRateRoleLabel = (role) => {
    switch (role) {
      case 'admin': return '본사';
      case 'dev': return '개발';
      case 'chief_major': return '총대본';
      case 'representative': return '대표자';
      case 'major_branch': return '대본사';
      case 'branch': return '부본사';
      case 'jisa': return '지사';
      case 'distributor': return '총판';
      case 'agency': return '매장';
      default: return role;
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'super': return 'role-badge super';
      case 'admin': return 'role-badge admin';
      case 'dev': return 'role-badge dev';
      case 'chief_major': return 'role-badge chief-major';
      case 'representative': return 'role-badge representative';
      case 'major_branch': return 'role-badge major-branch';
      case 'branch': return 'role-badge branch';
      case 'jisa': return 'role-badge jisa';
      case 'distributor': return 'role-badge distributor';
      case 'agency': return 'role-badge agency';
      default: return 'role-badge';
    }
  };

  const getUsersByRole = (role) => users.filter((u) => u.role === role);

  const handleRateRoleChange = (e) => {
    const nextRole = e.target.value;
    const candidates = users.filter((u) => u.role === nextRole);

    // 후보가 정확히 1명일 때만 자동 선택한다.
    // 예전에는 지사·총판·매장처럼 여러 명인 역할도 목록의 첫 번째를 자동으로 넣어
    // 엉뚱한 사용자에게 요율이 등록되기 쉬웠다.
    const nextLabel = candidates.length === 1 ? getUserDisplayName(candidates[0]) : '';

    setRateModal({ ...rateModal, role: nextRole, label: nextLabel });
  };

  const canManageUsers = ['super', 'admin'].includes(currentUser?.role);

  if (loading) {
    return (
      <div className="user-management-container">
        <div className="loading">사용자 목록을 불러오는 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-management-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="user-management-container">
      <div className="section-card">
        <div className="section-header">
          <div>
            <h2 className="section-title">사용자 관리</h2>
            <p className="section-subtitle">
              {currentUser?.role === 'super'
                ? '슈퍼어드민은 모든 사용자를 관리할 수 있습니다.'
                : '본사는 개발/총대본/대표자 및 조직(대본사~매장) 사용자를 관리할 수 있습니다.'}
            </p>
          </div>
          {canManageUsers && (
            <button className="create-user-btn" onClick={openCreateModal}>
              + 새 사용자 생성
            </button>
          )}
        </div>

        <div className="user-table-container">
          <table className="user-table">
            <thead>
              <tr>
                <th>번호</th>
                <th>역할</th>
                <th>아이디</th>
                <th>사용자명</th>
                <th>상위</th>
                <th>계좌정보</th>
                <th>가입일</th>
                {canManageUsers && <th>관리</th>}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={canManageUsers ? 8 : 7} className="no-data">사용자가 없습니다.</td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>
                      <span className={getRoleBadgeClass(user.role)}>
                        {getUserRoleLabel(user.role)}
                      </span>
                    </td>
                    <td>{user.username}</td>
                    <td>{user.company_name || user.company}</td>
                    <td>
                      {user.parent_username || user.parent_company_name
                        ? `${user.parent_company_name || user.parent_username}`
                        : '-'}
                    </td>
                    <td>{user.account || '-'}</td>
                    <td>{formatDate(user.created_at)}</td>
                    {canManageUsers && (
                      <td>
                        <div className="action-buttons">
                          <button
                            className="edit-user-btn"
                            onClick={() => openEditModal(user)}
                            disabled={
                              user.role === 'super' ||
                              ((user.role === 'admin' || user.role === 'dev') && currentUser?.role !== 'super') ||
                              (currentUser?.role === 'admin' && currentUser?.id === user.id)
                            }
                          >
                            수정
                          </button>
                        {/* 삭제 버튼: super는 모든 하위 역할, admin은 개발·조직 하위 역할만 */}
                          {(['super', 'admin'].includes(currentUser?.role) &&
                          (currentUser?.role === 'super' || (currentUser?.role === 'admin' && ['dev', 'chief_major', 'representative', 'major_branch', 'branch', 'jisa', 'distributor', 'agency'].includes(user.role)))) && (
                            <button
                              className="delete-user-btn"
                              onClick={() => handleDeleteUser(user)}
                              disabled={user.role === 'super'}
                            >
                              삭제
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="section-card">
        <div className="section-header">
          <div>
            <h3 className="section-title">분류별 요율 관리</h3>
            <p className="section-subtitle">분류별 역할/요율을 관리합니다.</p>
          </div>
        </div>

        {rateLoading ? (
          <div className="loading">요율 목록을 불러오는 중...</div>
        ) : rateError ? (
          <div className="error-message">{rateError}</div>
        ) : (
          <>
            {companySections.length === 0 ? (
              <div className="no-data">요율이 없습니다.</div>
            ) : (
              companySections.map((company) => {
              const items = rates.filter((rate) => (rate.company || '미지정') === company);
              const rateTotalPercent =
                items.length > 0 ? formatRateTotalPercent(items) : null;
              const rateCardExpanded = isRateCompanyExpanded(company);
              return (
                <div
                  key={company}
                  className="rate-company-card"
                  data-accordion-open={rateCardExpanded ? 'true' : 'false'}
                >
                  <div className="rate-company-header">
                    <button
                      type="button"
                      className="rate-company-header-toggle"
                      onClick={() => toggleRateCompanyCard(company)}
                      aria-expanded={rateCardExpanded}
                      aria-label={rateCardExpanded ? `${company} 분류 접기` : `${company} 분류 펼치기`}
                    >
                      <span className="rate-company-chevron" aria-hidden>{rateCardExpanded ? '▼' : '▶'}</span>
                      <div className="rate-company-header-left">
                        <div>
                          <span className="rate-company-label">분류</span>
                          <h4 className="rate-company-title">{company}</h4>
                        </div>
                        {rateTotalPercent != null && (
                          <span className="rate-company-total-sum" title="이 분류에 등록된 역할 요율의 합">
                            요율 합계 {rateTotalPercent}%
                          </span>
                        )}
                      </div>
                    </button>
                    {canManageUsers && (
                      <div className="rate-company-header-actions">
                        <button
                          className="reset-settlement-btn"
                          onClick={() => handleResetSettlementByCompany(company)}
                          disabled={resetSettlementLoading === company}
                          title="해당 분류의 일별정산 입력만 삭제합니다"
                        >
                          {resetSettlementLoading === company ? '처리 중...' : '일별정산 초기화'}
                        </button>
                        <button className="create-user-btn" onClick={() => openRateModal(null, company)}>
                          + 역할 추가
                        </button>
                      </div>
                    )}
                  </div>
                  {rateCardExpanded ? (
                    <div className="rate-company-card-body">
                      <table className="user-table">
                        <thead>
                          <tr>
                            <th>역할</th>
                            <th>사용자명</th>
                            <th>요율</th>
                            {canManageUsers && <th>관리</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {items.length === 0 ? (
                            <tr>
                              <td colSpan={canManageUsers ? 4 : 3} className="no-data">등록된 요율이 없습니다.</td>
                            </tr>
                          ) : (
                            items.map((rate) => (
                              <tr key={rate.id}>
                                <td>
                                  <span className={getRoleBadgeClass(rate.role)}>
                                    {getRateRoleLabel(rate.role)}
                                  </span>
                                </td>
                                <td>{rate.label}</td>
                                <td>{rate.rate}%</td>
                                {canManageUsers && (
                                  <td>
                                    <div className="action-buttons">
                                      <button
                                        className="edit-user-btn"
                                        onClick={() => openRateModal(rate)}
                                      >
                                        수정
                                      </button>
                                      <button
                                        className="delete-user-btn"
                                        onClick={() => handleDeleteRate(rate)}
                                      >
                                        삭제
                                      </button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              );
            })
            )}
          </>
        )}
      </div>

      {/* 사용자 생성 모달 */}
      {createModal.open && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>새 사용자 생성</h3>
              <button className="modal-close" onClick={closeCreateModal}>×</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label htmlFor="createUsername">아이디 *</label>
                <input
                  type="text"
                  id="createUsername"
                  value={createModal.username}
                  onChange={(e) => setCreateModal({...createModal, username: e.target.value})}
                  placeholder="아이디를 입력하세요"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="createPassword">비밀번호 *</label>
                <input
                  type="password"
                  id="createPassword"
                  value={createModal.password}
                  onChange={(e) => setCreateModal({...createModal, password: e.target.value})}
                  placeholder="비밀번호를 입력하세요 (최소 6자)"
                  minLength={6}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="createCompanyName">사용자명</label>
                <input
                  type="text"
                  id="createCompanyName"
                  value={createModal.company_name}
                  onChange={(e) => setCreateModal({...createModal, company_name: e.target.value})}
                  placeholder="사용자명을 입력하세요"
                />
              </div>
              <div className="form-group">
                <label htmlFor="createAccount">계좌정보</label>
                <input
                  type="text"
                  id="createAccount"
                  value={createModal.account}
                  onChange={(e) => setCreateModal({...createModal, account: e.target.value})}
                  placeholder="계좌정보를 입력하세요"
                  maxLength={100}
                />
              </div>
              <div className="form-group">
                <label htmlFor="createParentUser">상위 사용자 *</label>
                <select
                  id="createParentUser"
                  value={createModal.parent_user_id}
                  onChange={(e) => {
                    const nextParentId = e.target.value;
                    const childRoles = getChildRolesForParent(nextParentId);
                    const isValidRole = childRoles.includes(createModal.role);
                    setCreateModal({
                      ...createModal,
                      parent_user_id: nextParentId,
                      role: isValidRole ? createModal.role : (childRoles[0] || '')
                    });
                  }}
                  required
                >
                  <option value="">선택하세요</option>
                  {currentUser?.role === 'super' && (
                    <option value="__none__">없음 (본사·개발·총대본·대표자 전용)</option>
                  )}
                  {getParentOptionsForCreate().map((user) => (
                    <option key={user.id} value={user.id}>
                      {getUserDisplayName(user)} ({getUserRoleLabel(user.role)})
                    </option>
                  ))}
                </select>
                <span className="form-hint">본사·조직 계정은 상위를 선택하세요. 개발/총대본/대표자는 슈퍼만 상위 「없음」으로 만듭니다.</span>
              </div>
              <div className="form-group">
                <label htmlFor="createRole">역할 *</label>
                <select
                  id="createRole"
                  value={createModal.role}
                  onChange={(e) => setCreateModal({ ...createModal, role: e.target.value })}
                  required
                  disabled={!createModal.parent_user_id}
                >
                  <option value="">{createModal.parent_user_id ? '선택하세요' : '상위 사용자를 먼저 선택하세요'}</option>
                  {getChildRolesForParent(createModal.parent_user_id === '__none__' ? '' : createModal.parent_user_id).map((r) => (
                    <option key={r} value={r}>{getUserRoleLabel(r)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-btn" 
                onClick={closeCreateModal}
                disabled={createLoading}
              >
                취소
              </button>
              <button 
                className="confirm-btn" 
                onClick={handleCreateUser}
                disabled={
                  createLoading ||
                  !createModal.username ||
                  !createModal.password ||
                  !createModal.role ||
                  (!createModal.parent_user_id &&
                    createModal.role !== 'admin' &&
                    !SIDE_ROLES_FE.includes(createModal.role))
                }
              >
                {createLoading ? '생성 중...' : '생성'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 사용자 정보 수정 모달 */}
      {editModal.open && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>사용자 정보 수정</h3>
              <button className="modal-close" onClick={closeEditModal}>×</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label htmlFor="editUsername">아이디</label>
                <input
                  type="text"
                  id="editUsername"
                  value={editModal.username}
                  onChange={(e) => setEditModal({ ...editModal, username: e.target.value })}
                  placeholder="아이디를 입력하세요"
                />
              </div>
              <div className="form-group">
                <label htmlFor="editRole">역할</label>
                <input
                  type="text"
                  id="editRole"
                  value={getUserRoleLabel(editModal.user?.role)}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label htmlFor="editCompanyName">사용자명</label>
                <input
                  type="text"
                  id="editCompanyName"
                  value={editModal.company_name}
                  onChange={(e) => setEditModal({...editModal, company_name: e.target.value})}
                  placeholder="사용자명을 입력하세요"
                />
              </div>
              <div className="form-group">
                <label htmlFor="editAccount">계좌정보</label>
                <input
                  type="text"
                  id="editAccount"
                  value={editModal.account}
                  onChange={(e) => setEditModal({...editModal, account: e.target.value})}
                  placeholder="계좌정보를 입력하세요"
                  maxLength={100}
                />
              </div>
              <div className="form-group">
                <label htmlFor="editParentUser">상위 사용자</label>
                <select
                  id="editParentUser"
                  value={editModal.parent_user_id}
                  onChange={(e) => setEditModal({ ...editModal, parent_user_id: e.target.value })}
                >
                  <option value="">없음</option>
                  {getParentOptions(editModal.user?.role, editModal.user?.id).map((user) => (
                    <option key={user.id} value={user.id}>
                      {getUserDisplayName(user)} ({getUserRoleLabel(user.role)})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="editPassword">새 비밀번호 (선택사항)</label>
                <input
                  type="password"
                  id="editPassword"
                  value={editModal.newPassword}
                  onChange={(e) => setEditModal({...editModal, newPassword: e.target.value})}
                  placeholder="변경하지 않으려면 비워두세요 (최소 6자)"
                  minLength={6}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-btn" 
                onClick={closeEditModal}
                disabled={editLoading}
              >
                취소
              </button>
              <button 
                className="confirm-btn" 
                onClick={handleEditUser}
                disabled={editLoading}
              >
                {editLoading ? '처리 중...' : '수정'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 요율 생성/수정 모달 */}
      {rateModal.open && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>
                {rateModal.mode === 'edit'
                  ? '요율 수정'
                  : `역할 추가 - ${rateModal.company || '미지정'}`}
              </h3>
              <button className="modal-close" onClick={closeRateModal}>×</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label htmlFor="rateRole">역할 *</label>
                <select
                  id="rateRole"
                  value={rateModal.role}
                  onChange={handleRateRoleChange}
                  required
                >
                  <option
                    value="admin"
                    disabled={rates.some((rate) => rate.company === rateModal.company && rate.role === 'admin')}
                  >
                    본사
                  </option>
                  <option
                    value="dev"
                    disabled={rates.some((rate) => rate.company === rateModal.company && rate.role === 'dev')}
                  >
                    개발
                  </option>
                  <option
                    value="chief_major"
                    disabled={rates.some((rate) => rate.company === rateModal.company && rate.role === 'chief_major')}
                  >
                    총대본
                  </option>
                  <option
                    value="representative"
                    disabled={rates.some((rate) => rate.company === rateModal.company && rate.role === 'representative')}
                  >
                    대표자
                  </option>
                  <option
                    value="major_branch"
                    disabled={rates.some((rate) => rate.company === rateModal.company && rate.role === 'major_branch')}
                  >
                    대본사
                  </option>
                  <option
                    value="branch"
                    disabled={rates.some((rate) => rate.company === rateModal.company && rate.role === 'branch')}
                  >
                    부본사
                  </option>
                  <option value="jisa">지사</option>
                  <option value="distributor">총판</option>
                  <option value="agency">매장</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="rateLabel">사용자명 *</label>
                {/* 모든 역할을 동일하게 목록에서 고르게 한다.
                    (본사·개발도 계정이 둘 이상일 수 있어 자동 입력만으로는 지정이 불가능했다) */}
                <select
                  id="rateLabel"
                  value={rateModal.label}
                  onChange={(e) => setRateModal({ ...rateModal, label: e.target.value })}
                  required
                >
                  <option value="">사용자를 선택하세요</option>
                  {getUsersByRole(rateModal.role).map((user) => {
                    const displayName = getUserDisplayName(user);
                    // 같은 분류·역할에 이미 등록된 사용자는 중복 선택 불가 (수정 중인 행은 제외)
                    const alreadyUsed = rates.some(
                      (rate) => rate.company === rateModal.company &&
                        rate.role === rateModal.role &&
                        rate.label === displayName &&
                        rate.id !== rateModal.id
                    );
                    return (
                      <option key={user.id} value={displayName} disabled={alreadyUsed}>
                        {displayName}{alreadyUsed ? ' (등록됨)' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="rateValue">요율 (%) *</label>
                <input
                  type="number"
                  id="rateValue"
                  value={rateModal.rate}
                  onChange={(e) => setRateModal({ ...rateModal, rate: e.target.value })}
                  placeholder="0.00"
                  min="0"
                  max="100"
                  step="0.01"
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={closeRateModal} disabled={rateSaving}>
                취소
              </button>
              <button className="confirm-btn" onClick={handleSaveRate} disabled={rateSaving}>
                {rateSaving ? '처리 중...' : rateModal.mode === 'edit' ? '수정' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement; 